import React, { useEffect, useMemo, useState } from 'react';

function normaliseWhatsAppNumber(value) {
  let digits = String(value || '').replace(/\D/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2);
  if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
  if (digits.length === 10) digits = `91${digits}`;
  return /^\d{11,15}$/.test(digits) ? digits : '';
}

export function billingContacts(client = {}, snapshot = {}) {
  const source = { ...snapshot, ...client };
  const rows = [];
  if (source.mobile) rows.push({ name: source.contactPerson || source.contact || 'Primary contact', mobile: source.mobile, isPrimary: true });
  const extra = Array.isArray(source.additionalContacts) ? source.additionalContacts : [];
  extra.forEach((item, index) => {
    if (item?.mobile) rows.push({ name: item.name || `Contact ${index + 1}`, mobile: item.mobile, isPrimary: Boolean(item.isPrimary) });
  });
  const seen = new Set();
  return rows.filter((item) => {
    const number = normaliseWhatsAppNumber(item.mobile);
    if (!number || seen.has(number)) return false;
    seen.add(number);
    return true;
  }).sort((a, b) => Number(Boolean(b.isPrimary)) - Number(Boolean(a.isPrimary)));
}

export default function WhatsAppComposer({ open, title = 'WhatsApp', contacts = [], initialMessage = '', confirmLabel = 'Open WhatsApp', onClose, onConfirm }) {
  const [selected, setSelected] = useState('');
  const [message, setMessage] = useState(initialMessage);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setSelected(contacts[0]?.mobile || '');
    setMessage(initialMessage || '');
    setError('');
  }, [open, contacts, initialMessage]);

  const chosen = useMemo(() => contacts.find((item) => item.mobile === selected) || contacts[0] || null, [contacts, selected]);
  if (!open) return null;

  const confirm = () => {
    const phone = normaliseWhatsAppNumber(chosen?.mobile || selected);
    if (!phone) return setError('Select a valid saved mobile number.');
    if (!message.trim()) return setError('Message cannot be blank.');
    setError('');
    onConfirm?.({ phone, message: message.trim(), contact: chosen });
  };

  return <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-3">
    <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl">
      <div className="flex items-center justify-between border-b px-5 py-4">
        <div><h3 className="text-lg font-bold">{title}</h3><p className="text-xs text-gray-500">Choose a saved client contact, review the message and then open WhatsApp.</p></div>
        <button type="button" onClick={onClose} className="px-2 text-2xl">×</button>
      </div>
      <div className="space-y-4 p-5">
        {error && <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
        <label className="block text-sm">Client Contact
          <select className="mt-1 w-full rounded border px-3 py-2" value={selected} onChange={(e) => setSelected(e.target.value)}>
            {!contacts.length && <option value="">No valid saved mobile number</option>}
            {contacts.map((item) => <option key={`${item.mobile}-${item.name}`} value={item.mobile}>{item.name} · {item.mobile}{item.isPrimary ? ' · Primary' : ''}</option>)}
          </select>
        </label>
        <label className="block text-sm">Message Preview
          <textarea className="mt-1 w-full rounded border px-3 py-2" rows="9" value={message} onChange={(e) => setMessage(e.target.value)} />
        </label>
        <div className="rounded bg-amber-50 px-3 py-2 text-xs text-amber-900">Pravah opens WhatsApp with the message ready. You remain in control of the final Send action.</div>
      </div>
      <div className="flex justify-end gap-2 border-t px-5 py-4">
        <button type="button" onClick={onClose} className="rounded border px-4 py-2">Cancel</button>
        <button type="button" onClick={confirm} disabled={!contacts.length} className="rounded bg-green-600 px-4 py-2 font-medium text-white disabled:opacity-50">{confirmLabel}</button>
      </div>
    </div>
  </div>;
}
